/**
 * Normalize an API error into a single user-readable string.
 *
 * Handles the shapes this app actually produces/receives:
 *  - RTK Query / baseQuery error: `{ status, data, message }`
 *  - FastAPI error body `data.detail` as a **string**: "Release not found"
 *  - FastAPI 422 `data.detail` as an **array** of validation errors:
 *      [{ loc, msg, type }, ...]  → joined messages
 *  - a thrown `Error` (`err.message`)
 *  - a plain string
 *
 * Never leaks raw objects/stack traces to the UI; falls back to a generic
 * message so the caller always has something safe to display.
 *
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export const formatApiError = (err, fallback = 'Something went wrong. Please try again.') => {
  if (err == null) return fallback;
  if (typeof err === 'string') return err.trim() || fallback;

  // The error body may live at `err.data` (RTK) or be the body itself.
  const data = err.data ?? err;
  const detail = data?.detail;

  // FastAPI 422: detail is an array of { msg, loc, type }.
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        const msg = d?.msg || '';
        return field && msg ? `${field}: ${msg}` : msg || field;
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join('; ');
  }

  // FastAPI 4xx: detail is a plain string.
  if (typeof detail === 'string' && detail.trim()) return detail.trim();

  // Generic message fields.
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();

  return fallback;
};
